import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { api, type ListResponse } from 'src/common/utils/api';
import type { CalendarEvent } from 'src/meetings/models';
import { useFlags } from 'src/feature-flags/FlagsHook';

type CalendarEventsInput = {
  pastOrUpcoming: 'PAST' | 'UPCOMING';
  clientMeetingsOnly?: boolean;
  clientId?: string;
  clientMeetingsAndWithRecordingOnly?: boolean;
  enabled?: boolean;
};

type CalendarEventsPage = ListResponse<CalendarEvent> & { cutoff: string };

type PageParam = { page: number; cutoff?: string };

export const useCalendarEvents = ({
  pastOrUpcoming,
  clientMeetingsOnly = undefined,
  clientMeetingsAndWithRecordingOnly = false,
  clientId,
  enabled = true,
}: CalendarEventsInput) => {
  const { flagEnabledShowDesktopRecordings } = useFlags();
  const startOfMinute = new Date();
  startOfMinute.setSeconds(0, 0);

  const resource = clientId ? `clients/${clientId}/calendar-events` : 'calendar-events';
  const params = buildParams(
    pastOrUpcoming,
    startOfMinute,
    clientMeetingsOnly,
    clientId,
    clientMeetingsAndWithRecordingOnly,
  );

  const { data, isLoading, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery<
      CalendarEventsPage,
      Error,
      InfiniteData<CalendarEventsPage>,
      readonly unknown[],
      PageParam
    >({
      queryKey: flagEnabledShowDesktopRecordings
        ? [resource, params, 'precise-end-time']
        : [resource, params],
      queryFn: async ({ pageParam }) => {
        const cutoff =
          pageParam.cutoff ??
          (flagEnabledShowDesktopRecordings ? new Date() : startOfMinute).toISOString();
        const response = await api.getPaginated<CalendarEvent>(resource, {
          page: pageParam.page,
          size: 10,
          ...buildParams(
            pastOrUpcoming,
            new Date(cutoff),
            clientMeetingsOnly,
            clientId,
            clientMeetingsAndWithRecordingOnly,
          ),
        });
        return { ...response, cutoff };
      },
      initialPageParam: { page: 1 },
      enabled,
      getNextPageParam: (lastPage, allPages) => {
        const totalFetched = allPages.reduce((sum, page) => sum + page.items.length, 0);
        return totalFetched < lastPage.total
          ? { page: allPages.length + 1, cutoff: lastPage.cutoff }
          : undefined;
      },
    });

  const events = data?.pages.flatMap(page => page.items) ?? [];

  return {
    events,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};

function buildParams(
  pastOrUpcoming: 'PAST' | 'UPCOMING',
  date: Date,
  clientMeetingsOnly: boolean | undefined,
  clientId?: string,
  clientMeetingsAndWithRecordingOnly?: boolean,
): Record<string, unknown> {
  const params: Record<string, unknown> = {
    order: pastOrUpcoming === 'PAST' ? 'desc' : 'asc',
  };

  if (pastOrUpcoming === 'PAST') {
    params.endAt_lt = date.toISOString();
  } else {
    params.endAt_gte = date.toISOString();
  }

  if (!clientId && clientMeetingsOnly !== undefined) {
    params.clientMeetingsOnly = clientMeetingsOnly;
  }

  if (clientMeetingsAndWithRecordingOnly) {
    params.clientMeetingsAndWithRecordingOnly = true;
  }

  return params;
}
